import { type FieldMetadata, useInputControl } from '@conform-to/react'
import type * as LabelPrimitive from '@radix-ui/react-label'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import {
	Box,
	Flex,
	RadioGroup as RadixRadioGroup,
	Text,
} from '@radix-ui/themes'
import cn from 'clsx'
import { type TFunction } from 'i18next'
import * as React from 'react'

import { FormFieldError } from '~/components/ui/form-field-error.tsx'
import { Label } from '~/components/ui/label'

type RadioGroupItemElement = React.ElementRef<typeof RadixRadioGroup.Item>
type RadioGroupItemProps = RadioGroupPrimitive.RadioGroupItemProps &
	Pick<LabelPrimitive.LabelProps, 'htmlFor'>

/* -------------------------------------------------------------------------------------------------
 * RadioGroupRoot
 * -----------------------------------------------------------------------------------------------*/

type RadioGroupRootElement = React.ElementRef<typeof RadixRadioGroup.Root>
type RadioGroupRootProps = React.ComponentPropsWithoutRef<
	typeof RadixRadioGroup.Root
> & {
	children: React.ReactNode
	t: TFunction<string, undefined>
	meta: FieldMetadata<string>
}

const RadioGroupRoot = React.forwardRef<
	RadioGroupRootElement,
	RadioGroupRootProps
>((props, forwardedRef) => {
	const { meta, children, className, t, ...rest } = props

	const control = useInputControl(meta)

	return (
		<>
			<Flex direction="column" gapY="2">
				<RadixRadioGroup.Root
					ref={forwardedRef}
					value={control.value || meta.initialValue}
					onValueChange={control.change}
					onBlur={control.blur}
					className={cn('flex flex-col', className)}
					{...rest}
				>
					{children}
				</RadixRadioGroup.Root>
				{!!meta.errors && <FormFieldError error={meta.errors[0]} t={t} />}
			</Flex>
		</>
	)
})
RadioGroupRoot.displayName = 'RadioGroupRoot'

/* -------------------------------------------------------------------------------------------------
 * RadioGroupItem
 * -----------------------------------------------------------------------------------------------*/

const RadioGroupItem = React.forwardRef<
	RadioGroupItemElement,
	RadioGroupItemProps
>((props, forwardedRef) => {
	const { children, value } = props

	return (
		<label>
			<Flex align="center" gapX="2">
				<RadixRadioGroup.Item ref={forwardedRef} value={value} />
				<Text size="2" color="gray" className="ml-2">
					{children}
				</Text>
			</Flex>
		</label>
	)
})
RadioGroupItem.displayName = 'RadioGroupItem'

/* -------------------------------------------------------------------------------------------------
 * RadioGroupItem2
 * -----------------------------------------------------------------------------------------------*/

const RadioGroupItem2 = React.forwardRef<
	RadioGroupItemElement,
	RadioGroupItemProps
>((props, forwardedRef) => {
	const { children, className, htmlFor, ...rest } = props

	return (
		<Box className="app-Item">
			<RadioGroupPrimitive.Item
				{...rest}
				ref={forwardedRef}
				className={cn(
					'flex w-full flex-col items-center justify-start gap-y-3 rounded-md border-2 border-transparent bg-accent-2 p-3 shadow-md md:p-12',
					'transition duration-300 ease-in-out',
					'hover:border-accent-7',
					'radix-state-checked:border-accent-10 radix-state-checked:bg-accent-4',
					className,
				)}
			>
				{/*
        <div
          className="bg-gray-4 w-6 h-6 rounded-full shadow
        hover:bg-accent-4
        focus:shadow-xl"
        >
          <RadioGroupPrimitive.Indicator
            className="flex items-center justify-center w-full h-full relative
            after:content[''] after:block after:w-2.5 after:h-2.5 after:rounded-full after:bg-accent-8"
          />
        </div>
        */}
				<Label htmlFor={htmlFor} className="select-none text-gray-9">
					{children}
				</Label>
			</RadioGroupPrimitive.Item>
		</Box>
	)
})
RadioGroupItem2.displayName = 'RadioGroupItem2'

/* -------------------------------------------------------------------------------------------------
 * RadioGroupItem3
 * -----------------------------------------------------------------------------------------------*/

const RadioGroupItem3 = React.forwardRef<
	RadioGroupItemElement,
	RadioGroupItemProps
>((props, forwardedRef) => {
	const { children, className, htmlFor, ...rest } = props

	return (
		<RadioGroupPrimitive.Item
			{...rest}
			ref={forwardedRef}
			className={cn(
				'group h-10 w-10 rounded-full bg-gray-2 text-gray-9 shadow',
				'transition duration-300 ease-in-out',
				'hover:bg-accent-6 hover:text-gray-2',
				'focus:shadow-xl',
				className,
			)}
		>
			<RadioGroupPrimitive.Indicator className="after:content[''] relative flex h-full w-full items-center justify-center after:block after:h-2.5 after:w-2.5 after:rounded-full after:bg-accent-8" />
			<Label
				htmlFor={htmlFor}
				className={cn(
					'select-none',
					'transition duration-300 ease-in-out',
					'hover:text-gray-2',
				)}
			>
				{children}
			</Label>
		</RadioGroupPrimitive.Item>
	)
})
RadioGroupItem3.displayName = 'RadioGroupItem3'

/* -----------------------------------------------------------------------------------------------*/

const RadioGroup = {
	Root: RadioGroupRoot,
	Item: RadioGroupItem,
	Item2: RadioGroupItem2,
	Item3: RadioGroupItem3,
}

export {
	RadioGroup,
	RadioGroupRoot,
	RadioGroupItem,
	RadioGroupItem2,
	RadioGroupItem3,
}
